import { Button } from "@/core/components/ui/button";
import { Textarea } from "@/core/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Loader2, Plus, Send, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChatInputTypes } from "../types";
import { parseEmailsFromSpreadsheet } from "../utils/parseEmailsFromSpreadsheet";

const ACCEPTED_FILE_TYPES =
  ".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv";

export function ChatInput({
  value,
  disabled,
  onChange,
  onSend,
  onEnter,
  variant = "default",
  onNewConversation,
  allowedDomains = [],
  onFileParsed,
}: ChatInputTypes) {
  const [translate] = useTranslation(["core"]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleSend = () => {
    if (!disabled && value.trim()) {
      onSend();
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = variant === "action-center" ? 150 : 200;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [value, variant]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (onEnter) {
        onEnter(e as React.KeyboardEvent);
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onFileParsed) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext || "")) {
      setParseError("Formato não suportado. Use CSV ou Excel (.xlsx, .xls).");
      return;
    }

    setIsParsing(true);
    setParseError(null);
    try {
      const result = await parseEmailsFromSpreadsheet(file, allowedDomains);
      onFileParsed({ ...result, fileName: file.name, fileSize: file.size });
    } catch (err) {
      setParseError(
        err instanceof Error ? err.message : "Erro ao processar o arquivo.",
      );
    } finally {
      setIsParsing(false);
    }
  };

  const isActionCenter = variant === "action-center";

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={
          isActionCenter
            ? "mx-auto w-full max-w-[673px] flex-shrink-0 px-4 pt-2"
            : "flex-shrink-0 rounded-b-[64px] bg-white p-[24px] pt-[16px]"
        }
      >
        {parseError && (
          <p className="mb-2 text-sm text-red-600">{parseError}</p>
        )}
        <div
          className={
            isActionCenter
              ? "flex flex-col gap-4 rounded-3xl border border-border border-warmgray-100 bg-white p-4"
              : "flex flex-row items-end gap-4 rounded-3xl border border-warmgray-100 p-[16px]"
          }
        >
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={translate("chatbot-placeholder") as string}
            className={
              isActionCenter
                ? "m-0 min-h-[38px] w-full resize-none overflow-y-auto border-0 p-2 text-sm leading-[21px] focus-visible:ring-0"
                : "m-0 min-h-[56px] max-h-[200px] flex-1 overflow-y-auto border-0 p-2 text-[16px] focus-visible:ring-0"
            }
            style={isActionCenter ? { maxHeight: 150 } : undefined}
            disabled={disabled}
            rows={1}
          />
          <div
            className={
              isActionCenter
                ? "flex items-center justify-between gap-3"
                : "flex gap-3"
            }
          >
            {isActionCenter && onNewConversation ? (
              <Button
                type="button"
                variant="ghost"
                onClick={onNewConversation}
                className="h-8 gap-2 rounded-lg px-2 text-xs font-medium text-foreground hover:bg-muted"
              >
                <Plus className="size-4" />
                {translate("chatbot-history-new-conversation")}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              {onFileParsed && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    onChange={handleFileChange}
                    className="hidden"
                    aria-hidden
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={
                          isActionCenter
                            ? "size-8 shrink-0 rounded-lg border-foreground p-0 text-foreground hover:bg-muted"
                            : "group h-[56px] min-w-[56px] max-w-12 shrink-0 gap-0 overflow-hidden rounded-2xl border-darkgray-700 text-darkgray-700 transition-[max-width] duration-200 ease-out hover:max-w-[250px] hover:gap-2 hover:bg-lightgray-200"
                        }
                        onClick={() => fileInputRef.current?.click()}
                        disabled={disabled || isParsing}
                        aria-label="Importar colaboradores"
                      >
                        {isParsing ? (
                          <Loader2
                            className={
                              isActionCenter
                                ? "size-4 shrink-0 animate-spin"
                                : "h-6 w-6 shrink-0 animate-spin"
                            }
                          />
                        ) : (
                          <Upload
                            className={
                              isActionCenter
                                ? "size-4 shrink-0"
                                : "h-6 w-6 shrink-0"
                            }
                          />
                        )}
                        {!isActionCenter && (
                          <span className="inline-block max-w-0 overflow-hidden transition-[max-width] duration-200 ease-out group-hover:max-w-[180px]">
                            <span className="whitespace-nowrap pl-0">
                              Importar colaboradores
                            </span>
                          </span>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="text-xs bg-lightgray-200 text-neutral-900 border-lightgray-300"
                    >
                      Anexar arquivo csv ou xlsx
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              <Button
                onClick={handleSend}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                disabled={!value.trim() || disabled}
                className={
                  isActionCenter
                    ? "size-8 min-h-8 min-w-8 shrink-0 rounded-lg bg-darkgray-700 p-0 text-white hover:bg-darkgray-600 disabled:bg-lightgray-600 disabled:text-darkgray-200"
                    : "min-h-[56px] min-w-[56px] shrink-0 rounded-2xl bg-darkgray-700 text-white hover:bg-darkgray-600 disabled:bg-lightgray-600 disabled:text-darkgray-200"
                }
                type="button"
              >
                {disabled ? (
                  <Loader2
                    className={
                      isActionCenter
                        ? "size-4 animate-spin"
                        : "h-6 w-6 animate-spin"
                    }
                  />
                ) : (
                  <Send className={isActionCenter ? "size-4" : "h-6 w-6"} />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
